'use client';

import { useEffect, useRef, useState } from 'react';
import { z } from 'zod';
import type { Payment, PaymentStatus } from '@/core/domain';
import { PAYMENT_STATUS_LABELS } from '@/core/domain';
import { VERIFY_SUBSCRIPTION_SUCCESS_REDIRECT_MS } from '@/features/payments/presentation/composables/constants/verify-subscription.constants';
import {
  VERIFY_SUBSCRIPTION_LISTEN_ERROR_PREFIX,
} from '@/features/payments/presentation/constants/verify-subscription-copy';

export type VerifySubscriptionPaymentPhase =
  | 'invalid_payment_id'
  | 'unauthenticated'
  | 'listening'
  | 'success'
  | 'payment_failed'
  | 'not_found'
  | 'listen_error';

const FAILURE_STATUSES: ReadonlySet<PaymentStatus> = new Set([
  'rejected',
  'cancelled',
  'refunded',
  'charged_back',
]);

function isTerminalFailure(status: PaymentStatus): boolean {
  return FAILURE_STATUSES.has(status);
}

function isApproved(status: PaymentStatus): boolean {
  return status === 'approved';
}

const uuidParamSchema = z.string().uuid();

export interface UseVerifySubscriptionPaymentParams {
  paymentIdParam: string | null;
  authReady: boolean;
  userId: string | undefined;
  subscribePaymentDoc: (
    paymentId: string,
    onNext: (payment: Payment | null) => void,
    onError: (error: Error) => void
  ) => () => void;
  /** Chamado após `VERIFY_SUBSCRIPTION_SUCCESS_REDIRECT_MS` em `approved`. */
  onRedirectToApp: () => void | Promise<void>;
}

export interface UseVerifySubscriptionPaymentResult {
  phase: VerifySubscriptionPaymentPhase;
  /** Mensagem extra em falhas (detalhe do gateway ou erro de escuta). */
  detailMessage: string | null;
}

export function useVerifySubscriptionPayment({
  paymentIdParam,
  authReady,
  userId,
  subscribePaymentDoc,
  onRedirectToApp,
}: UseVerifySubscriptionPaymentParams): UseVerifySubscriptionPaymentResult {
  const [phase, setPhase] = useState<VerifySubscriptionPaymentPhase>('listening');
  const [detailMessage, setDetailMessage] = useState<string | null>(null);
  const redirectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const unsubscribeRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    if (redirectTimerRef.current) {
      clearTimeout(redirectTimerRef.current);
      redirectTimerRef.current = null;
    }
    if (unsubscribeRef.current) {
      unsubscribeRef.current();
      unsubscribeRef.current = null;
    }

    if (!authReady) {
      setPhase('listening');
      setDetailMessage(null);
      return;
    }

    if (!userId) {
      setPhase('unauthenticated');
      setDetailMessage(null);
      return;
    }

    const parsed = uuidParamSchema.safeParse(
      paymentIdParam != null && paymentIdParam.trim().length > 0 ? paymentIdParam.trim() : ''
    );
    if (!parsed.success) {
      setPhase('invalid_payment_id');
      setDetailMessage(null);
      return;
    }

    const paymentId = parsed.data;
    setPhase('listening');
    setDetailMessage(null);

    const unsub = subscribePaymentDoc(
      paymentId,
      (payment) => {
        if (payment == null) {
          setPhase('not_found');
          setDetailMessage(null);
          return;
        }
        if (isApproved(payment.status)) {
          setPhase('success');
          setDetailMessage(null);
          if (redirectTimerRef.current) clearTimeout(redirectTimerRef.current);
          redirectTimerRef.current = setTimeout(() => {
            redirectTimerRef.current = null;
            void Promise.resolve(onRedirectToApp());
          }, VERIFY_SUBSCRIPTION_SUCCESS_REDIRECT_MS);
          return;
        }
        if (isTerminalFailure(payment.status)) {
          const label = PAYMENT_STATUS_LABELS[payment.status];
          const detail =
            payment.status_detail != null && payment.status_detail.trim().length > 0 ?
              payment.status_detail.trim()
            : null;
          setPhase('payment_failed');
          setDetailMessage(detail != null ? `${label}: ${detail}` : label);
          return;
        }
      },
      (err) => {
        setPhase('listen_error');
        setDetailMessage(`${VERIFY_SUBSCRIPTION_LISTEN_ERROR_PREFIX} ${err.message}`);
      }
    );
    unsubscribeRef.current = unsub;

    return () => {
      if (redirectTimerRef.current) {
        clearTimeout(redirectTimerRef.current);
        redirectTimerRef.current = null;
      }
      unsub();
      unsubscribeRef.current = null;
    };
  }, [authReady, userId, paymentIdParam, subscribePaymentDoc, onRedirectToApp]);

  return { phase, detailMessage };
}
